import { useState, type FormEvent } from 'react'
import { Search as SearchIcon } from 'lucide-react'
import { EmptyState } from '../../components/ui/empty-state'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { api, type components } from '../../api/client'
import { usersQuery } from '../../lib/users'
import { dmTitle, isDirect } from '../../lib/channels'
import { Avatar } from '../../components/avatar'
import { Markdown } from '../../components/messages/markdown'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { SearchFilters } from '../../components/search-filters'
import { composeQuery, parseQuery } from '../../lib/search-query'
import { createSavedSearch, savedSearchesQuery } from '../../lib/saved-searches'
import { CategoryNameDialog } from '../../components/sidebar-sections'
import { useQueryClient } from '@tanstack/react-query'
import { Bookmark } from 'lucide-react'

// DIS-59: search results grouped by channel, filter chips, no cap (keep paging).

type Hit = { message: components['schemas']['Message']; channel: components['schemas']['Channel']; score: number; file_match?: { name: string; snippet: string } }

export const Route = createFileRoute('/_app/search')({
  validateSearch: (s: Record<string, unknown>): { q?: string; semantic?: boolean } => ({ ...(typeof s.q === 'string' && s.q ? { q: s.q } : {}), ...(s.semantic === true || s.semantic === 'true' || s.semantic === '1' ? { semantic: true } : {}) }),
  component: SearchPage,
})

function SearchPage() {
  const { q = '', semantic = false } = Route.useSearch()
  const { me } = Route.useRouteContext()
  const navigate = useNavigate()
  const [draft, setDraft] = useState(q)
  const qc = useQueryClient()
  const saved = useQuery(savedSearchesQuery)
  const [saveOpen, setSaveOpen] = useState(false)
  // The box and the URL can drift while typing; the filter bar always
  // reflects what is being searched (the URL).
  const filters = parseQuery(q)
  const applyFilters = (f: Parameters<typeof composeQuery>[0]) => {
    const next = composeQuery({ ...f, text: parseQuery(draft).text || f.text })
    setDraft(next)
    void navigate({ to: '/search', search: next ? { q: next, ...(semantic ? { semantic: true } : {}) } : {} })
  }
  const alreadySaved = (saved.data ?? []).some((s) => s.query === q)
  const users = useQuery(usersQuery)
  const byId = new Map((users.data ?? []).map((u) => [u.id, u]))
  const results = useInfiniteQuery({
    queryKey: ['search', q, semantic],
    enabled: !!q,
    initialPageParam: '',
    queryFn: async ({ pageParam }) => {
      const { data, error, response } = await api.GET('/search', { params: { query: { q, limit: 25, ...(semantic ? { semantic: true } : {}), ...(pageParam ? { cursor: pageParam } : {}) } } })
      if (!data) throw new Error((error as { fields?: Record<string, string>; message?: string } | undefined)?.fields?.q ?? (error as { message?: string } | undefined)?.message ?? `search failed (${response.status})`)
      return data
    },
    getNextPageParam: (last) => last.next_cursor || undefined,
    staleTime: 30_000,
  })
  const hits: Hit[] = (results.data?.pages ?? []).flatMap((p) => p.hits as Hit[])
  const parsed = results.data?.pages[0]?.query
  const chips: string[] = []
  if (parsed?.from) chips.push(`from:@${parsed.from}`)
  if (parsed?.in) chips.push(`in:#${parsed.in}`)
  if (parsed?.before) chips.push(`before:${String(parsed.before).slice(0, 10)}`)
  if (parsed?.after) chips.push(`after:${String(parsed.after).slice(0, 10)}`)
  if (parsed?.has_file) chips.push('has:file')
  if (parsed?.has_image) chips.push('has:image')
  if (parsed?.has_link) chips.push('has:link')

  // Group consecutive hits by channel (ranking order preserved within groups).
  const groups: Array<{ channel: Hit['channel']; hits: Hit[] }> = []
  for (const h of hits) {
    const last = groups[groups.length - 1]
    if (last && last.channel.id === h.channel.id) last.hits.push(h)
    else groups.push({ channel: h.channel, hits: [h] })
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    const next = draft.trim()
    void navigate({ to: '/search', search: next ? { q: next, ...(semantic ? { semantic: true } : {}) } : {} })
  }
  const title = (c: Hit['channel']) => (isDirect(c) ? dmTitle(c, me.id, users.data ?? []) : `#${c.name}`)
  const linkTo = (h: Hit) => (isDirect(h.channel) ? { to: '/dm/$id' as const, params: { id: h.channel.id }, search: { thread: undefined } } : { to: '/c/$slug/$messageId' as const, params: { slug: h.channel.slug ?? h.channel.id, messageId: h.message.id }, search: {} })

  return (
    <>
      <header className="flex h-12 items-center gap-3 border-b border-border px-4" data-testid="channel-header">
        <h1 className="shrink-0 font-semibold">Search</h1>
        <form onSubmit={submit} className="flex flex-1 items-center gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder='deploy from:@sam in:#ops before:2026-08-01 "exact phrase" has:file' aria-label="Search messages" className="h-8 flex-1" autoFocus />
          <Button type="submit" size="sm" disabled={!draft.trim()}>
            Search
          </Button>
        </form>
        {q && (
          <Button size="sm" variant={alreadySaved ? 'secondary' : 'ghost'} onClick={() => setSaveOpen(true)} disabled={alreadySaved} aria-label="Save search" title={alreadySaved ? 'Already saved' : 'Save this search'}>
            <Bookmark size={14} /> {alreadySaved ? 'Saved' : 'Save'}
          </Button>
        )}
        <label className="flex items-center gap-1.5 text-xs text-fg-muted" title="Also rank by meaning (embeddings); needs VOYAGE_API_KEY on the server">
          <input type="checkbox" checked={semantic} onChange={(e) => void navigate({ to: '/search', search: { ...(q ? { q } : {}), ...(e.target.checked ? { semantic: true } : {}) } })} data-testid="semantic-toggle" />
          Semantic
        </label>
      </header>
      <SearchFilters value={filters} onChange={applyFilters} />
      <CategoryNameDialog open={saveOpen} onClose={() => setSaveOpen(false)} title="Save search" onSubmit={async (name) => { await createSavedSearch(qc, name, q, semantic) }} />
      <div className="flex-1 overflow-y-auto p-4" data-testid="search-results">
        {chips.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1" data-testid="search-chips">
            {chips.map((c) => (
              <span key={c} className="rounded-full border border-border bg-bg-muted px-2 py-0.5 font-mono text-xs">
                {c}
              </span>
            ))}
            {parsed?.text && <span className="px-2 py-0.5 text-xs text-fg-muted">matching “{parsed.text}”</span>}
          </div>
        )}
        {!q && (saved.data?.length ?? 0) > 0 && (
          <div className="mx-auto mb-2 flex max-w-sm flex-wrap justify-center gap-1.5" data-testid="saved-search-chips">
            {saved.data!.map((s) => (
              <Link key={s.id} to="/search" search={{ q: s.query, ...(s.semantic ? { semantic: true } : {}) }} className="rounded-full border border-border px-2.5 py-0.5 text-[12px] text-fg-muted hover:border-border-strong hover:text-fg">
                {s.name}
              </Link>
            ))}
          </div>
        )}
        {!q && (
          <EmptyState
            icon={<SearchIcon size={16} />}
            title="Search every channel you can read"
            hint={<span>Filters: <code className="rounded bg-bg-muted px-1">from:@handle</code> <code className="rounded bg-bg-muted px-1">in:#channel</code> <code className="rounded bg-bg-muted px-1">before:2026-01-31</code> <code className="rounded bg-bg-muted px-1">has:file</code> <code className="rounded bg-bg-muted px-1">"exact phrase"</code> <code className="rounded bg-bg-muted px-1">-excluded</code></span>}
          />
        )}
        {results.isError && (
          <p role="alert" className="text-sm text-danger">
            {results.error.message}
          </p>
        )}
        {q && results.isSuccess && hits.length === 0 && <EmptyState title={`No messages match “${q}”`} hint="Try fewer words, drop a filter, or turn on Semantic to search by meaning." />}
        <div className="mx-auto max-w-3xl space-y-4">
          {groups.map((g, gi) => (
            <section key={`${g.channel.id}-${gi}`} data-testid="search-group">
              <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {title(g.channel)} <span className="font-normal">· {g.hits.length}</span>
              </h2>
              <ul className="divide-y divide-border rounded-md border border-border">
                {g.hits.map((h) => {
                  const author = h.message.author_id ? byId.get(h.message.author_id) : undefined
                  return (
                    <li key={h.message.id} data-testid="search-hit">
                      <Link {...linkTo(h)} className="block px-3 py-2 hover:bg-bg-muted/60">
                        <div className="mb-0.5 flex items-center gap-2 text-xs text-fg-muted">
                          {author && <Avatar user={author} size={16} />}
                          <span className="font-medium text-fg">{author?.display_name ?? 'Unknown'}</span>
                          <span>{new Date(h.message.created_at).toLocaleString()}</span>
                          {h.message.attachments.length > 0 && <span>📎 {h.message.attachments.length}</span>}
                        </div>
                        <Markdown source={h.message.body} />
                        {h.file_match && (
                          <p className="mt-1 truncate text-xs text-fg-muted" data-testid="file-match">
                            📄 <span className="font-medium text-fg">{h.file_match.name}</span>: <Snippet html={h.file_match.snippet} />
                          </p>
                        )}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
        {results.hasNextPage && (
          <div className="py-4 text-center">
            <Button variant="secondary" size="sm" onClick={() => results.fetchNextPage()} disabled={results.isFetchingNextPage} data-testid="search-more">
              {results.isFetchingNextPage ? 'Loading…' : 'More results'}
            </Button>
          </div>
        )}
        {q && results.isSuccess && !results.hasNextPage && hits.length > 0 && <p className="py-4 text-center text-xs text-fg-muted">{hits.length} results — that's everything.</p>}
      </div>
    </>
  )
}

/** ts_headline output marks hits with <b>…</b>; render that and nothing else. */
function Snippet({ html }: { html: string }) {
  const parts = html.split(/(<b>.*?<\/b>)/)
  return (
    <>
      {parts.map((p, i) => (p.startsWith('<b>') ? <mark key={i} className="rounded bg-accent-soft px-0.5 text-fg">{p.slice(3, -4)}</mark> : <span key={i}>{p}</span>))}
    </>
  )
}
